<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEditions } from '@/composables/useEditions'
import { useEvents } from '@/composables/useEvents'
import { useRaces } from '@/composables/useRaces'
import RaceForm from '@/components/organizer/events/RaceForm.vue'
import type { DraftRacePriceTier, StoreRacePayload } from '@/types/events'
import RacePriceTiersSection from '@/components/organizer/events/RacePriceTiersSection.vue'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import PayoutNotReadyNotice from '@/components/organizer/payouts/PayoutNotReadyNotice.vue'
import { payoutNotReadyMessage } from '@/api/payouts'

interface RaceFormData {
  name: string
  type: '' | 'road' | 'trail' | 'track' | 'duathlon' | 'triathlon' | 'cycling' | 'virtual' | 'other'
  base_price: string
  distance: string
  elevation: string
  start_venue_id: number | null
  end_venue_id: number | null
  start_time: string
  cutoff: string
  max_participants: string
  allow_waiting_list: boolean
  payment_deadline_hours: string
  registrations_closing_time: string
  registrations_open: boolean
  sort_order: string
}

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { event, fetchEvent } = useEvents()
const { edition: fetchedEdition, fetchEdition } = useEditions()
const { createRace } = useRaces()

const loading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const payoutNotReady = ref<string | null>(null)

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string

// The venue pickers are organisation-scoped and a race is one hop deeper than
// an edition (race -> edition -> event -> organisation). The event's own
// organisation_id is the authoritative source; the auth store is not consulted.
// The edition ref is shared module state and may still hold the previously
// viewed edition; ignore it until its slug matches this route's edition.
const edition = computed(() =>
  fetchedEdition.value?.slug === editionSlug ? fetchedEdition.value : null,
)

const organisationId = computed<number | null>(() => event.value?.organisation_id ?? null)

onMounted(() => {
  void fetchEvent(eventSlug)
  void fetchEdition(eventSlug, editionSlug)
})

const form = ref<RaceFormData>({
  name: '',
  type: '',
  base_price: '',
  distance: '',
  elevation: '',
  start_venue_id: null,
  end_venue_id: null,
  start_time: '',
  cutoff: '',
  max_participants: '',
  allow_waiting_list: false,
  payment_deadline_hours: '',
  registrations_closing_time: '',
  registrations_open: true,
  sort_order: '',
})

// Unsaved pricing tiers; kept locally so they survive a failed submit.
const draftTiers = ref<DraftRacePriceTier[]>([])

const onlyTierErrors = computed<boolean>(() => {
  const keys = Object.keys(errors.value)
  return keys.length > 0 && keys.every((key) => key === 'price_tiers' || key.startsWith('price_tiers.'))
})

async function handleSubmit(data: StoreRacePayload): Promise<void> {
  errors.value = {}
  generalError.value = ''
  payoutNotReady.value = null
  loading.value = true
  const payload: StoreRacePayload = { ...data }
  if (draftTiers.value.length > 0) {
    payload.price_tiers = draftTiers.value.map((tier) => ({
      name: tier.name,
      price: Number(tier.price),
      ends_at: tier.ends_at,
      max_registrations: tier.max_registrations,
    }))
  }
  try {
    const created = await createRace(eventSlug, editionSlug, payload)
    void router.push(`/organizer/events/${eventSlug}/editions/${editionSlug}/races/${created.slug}/edit`)
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      const notReady = payoutNotReadyMessage(e)
      if (notReady !== null) {
        payoutNotReady.value = notReady
      } else if (e.response.status === 422 && e.response.data.errors) {
        errors.value = e.response.data.errors
      } else {
        generalError.value = t('races.form.error_generic')
      }
    } else {
      generalError.value = t('races.form.error_generic')
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div class="mb-6">
        <RouterLink
          :to="`/organizer/events/${eventSlug}/editions/${editionSlug}`"
          class="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1"
        >
          ← {{ t('editions.detail.back_to_event') }}
        </RouterLink>
      </div>

      <h1 class="text-2xl font-bold text-white mb-6">{{ t('races.form.create_heading') }}</h1>

      <PayoutNotReadyNotice v-if="payoutNotReady" :message="payoutNotReady" />

      <div
        v-if="generalError"
        class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
      >
        {{ generalError }}
      </div>

      <div
        v-if="onlyTierErrors"
        class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
        role="alert"
        data-testid="price-tiers-create-errors-banner"
      >
        {{ t('races.price_tiers.create_errors_banner') }}
      </div>

      <AppSpinner v-if="organisationId === null" />

      <RaceForm
        v-else
        v-model="form"
        :loading="loading"
        :errors="errors"
        :organisation-id="organisationId"
        :edition-venue-timezone="edition?.venue?.timezone ?? null"
        :edition-venue="edition?.venue ?? null"
        :is-edit="false"
        @submit="handleSubmit"
        @cancel="router.push(`/organizer/events/${eventSlug}/editions/${editionSlug}`)"
      >
        <template #price-tiers="{ timezone }">
          <RacePriceTiersSection
            v-model="draftTiers"
            mode="draft"
            :organisation-id="organisationId"
            :timezone="timezone"
            :standard-price="form.base_price"
            :draft-errors="errors"
          />
        </template>
      </RaceForm>
    </main>
  </OrganizerLayout>
</template>
